import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Role } from '@data/sdk/match';
import { useTheme } from '@ui/hooks';

import { paths } from './constants';
import QuestionPage from './pages/b2b/QuestionsLibrary/pages/[id]';
import QuestionClassificationPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification';
import QuestionIcdCodesPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification/pages/IcdCodes';
import QuestionStartersPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification/pages/Starters';
import QuestionTagsPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification/pages/Tags';
import QuestionDetailsPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Details';
import QuestionMergePage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Merge';
import QuestionTrialsPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Trials';
import { trialPageLoader } from './pages/b2b/TrialRecruitment/TrialPage';
import TrialsLibraryPage from './pages/b2b/TrialsLibrary';
import TrialDetailsPage from './pages/b2b/TrialsLibrary/pages/[id]';
import { FixedLayout, Layout } from './pages/patient/components';
import { ProfileLayout } from './pages/patient/Profile/components';
import { PublicPageLayout, PublicPageLayoutV2 } from './pages/public';

import { lazyWithRetry, PrivateRoute, queryClient } from './utils';

// Shared pages
const Notifications = lazyWithRetry(
  () => import('./pages/shared/Notifications/Notifications'),
  'Notifications',
);

// Patient profile router
const GeneralPage = lazyWithRetry(() => import('./pages/patient/Profile/General'), 'GeneralPage');
const PasswordPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Password'),
  'PasswordPage',
);
const DiseasesPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Diseases'),
  'DiseasesPage',
);
const AttributesPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Attributes'),
  'AttributesPage',
);
const PatientNotificationsSettingsPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Notifications'),
  'PatientNotificationsSettingsPage',
);

// Patient router
const AccountProgressionPage = lazyWithRetry(
  () => import('./pages/patient/AccountProgression'),
  'AccountProgressionPage',
);
const ChatPage = lazyWithRetry(() => import('./pages/shared/Chat'), 'ChatPage');
const DeleteAccount = lazyWithRetry(
  () => import('./pages/patient/Profile/DeleteAccount'),
  'DeleteAccount',
);
const TrialsListPage = lazyWithRetry(() => import('./pages/patient/Trials/List'), 'TrialsListPage');
const TrialPage = lazyWithRetry(() => import('./pages/patient/Trials/[id]'), 'TrialPage');
const PatientSurveyPage = lazyWithRetry(
  () => import('./pages/patient/Trials/[surveyId]'),
  'PatientSurveyPage',
);

// B2B trial recruitment router
const B2BTrialsPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage'),
  'B2BTrialsPage',
);
const B2BTrialsListPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage/TrialsList'),
  'B2BTrialsListPage',
);
const RequestsListPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage/RequestsList'),
  'RequestsListPage',
);
const BulkInvitesList = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage/BulkInvitesList'),
  'BulkInvitesList',
);
const BulkInvitePage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/BulkInvite'),
  'BulkInvitePage',
);
const RequestTrialPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/RequestTrialPage'),
  'RequestTrialPage',
);
const EligibilityCriteriaPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/EligibilityCriteria'),
  'EligibilityCriteriaPage',
);
const MatchingVolunteersPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/MatchingVolunteers'),
  'MatchingVolunteersPage',
);
const VolunteerDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/MatchingVolunteers/VolunteerDetails'),
  'VolunteerDetailsPage',
);
const AdminNotificationSettings = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Notifications'),
  'AdminNotificationSettings',
);

const AdminTrialTeams = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Teams'),
  'AdminTrialTeams',
);

// B2B profile router
const GeneralFormPage = lazyWithRetry(
  () => import('./pages/b2b/Profile/GeneralForm'),
  'GeneralFormPage',
);
const PasswordFormPage = lazyWithRetry(
  () => import('./pages/b2b/Profile/PasswordForm'),
  'PasswordFormPage',
);

// B2B hospitals router
const HospitalsListPage = lazyWithRetry(
  () => import('./pages/b2b/Hospitals/List'),
  'HospitalsListPage',
);
const HospitalDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/Hospitals/[id]'),
  'HospitalDetailsPage',
);
const CreateHospital = lazyWithRetry(
  () => import('./pages/b2b/Hospitals/Create'),
  'CreateHospital',
);

// B2B diseases router
const DiseasesListPage = lazyWithRetry(
  () => import('./pages/b2b/Diseases/List'),
  'DiseasesListPage',
);
const DiseaseDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/Diseases/[id]'),
  'DiseaseDetailsPage',
);

// B2B router
const B2BLayout = lazyWithRetry(() => import('./pages/b2b/components/B2BLayout'), 'B2BLayout');
const CreateTeam = lazyWithRetry(() => import('./pages/b2b/Team/Create'), 'CreateTeam');
const B2BProfileLayout = lazyWithRetry(
  () => import('./pages/b2b/Profile/components/ProfileLayout'),
  'B2BProfileLayout',
);
const TeamsList = lazyWithRetry(() => import('./pages/b2b/Team/TeamsList'), 'TeamsList');
const TeamSettingsLayout = lazyWithRetry(
  () => import('./pages/b2b/Team/TeamSettingsLayout'),
  'TeamSettingsLayout',
);
const TeamGeneral = lazyWithRetry(() => import('./pages/b2b/Team/TeamGeneral'), 'TeamGeneral');
const TeamUsers = lazyWithRetry(() => import('./pages/b2b/Team/TeamUsers'), 'TeamUsers');
const TeamBilling = lazyWithRetry(() => import('./pages/b2b/Team/TeamBilling'), 'TeamBilling');
const TeamTrials = lazyWithRetry(() => import('./pages/b2b/Team/TeamTrials'), 'TeamTrials');
const OnboardingPage = lazyWithRetry(() => import('./pages/b2b/Onboarding'), 'OnboardingPage');
const B2BTrialPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage'),
  'B2BTrialPage',
);

// B2B questions library router
const QuestionsLibraryPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary'),
  'QuestionsLibraryPage',
);
const QuestionActivityPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary/pages/[id]/pages/Activity'),
  'QuestionActivityPage',
);
const CreateQuestionPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary/pages/Create'),
  'CreateQuestionPage',
);
const QuestionMergeEditorPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary/pages/[id]/pages/Merge/pages/Editor'),
  'QuestionMergeEditorPage',
);

// B2B users router
const UsersPage = lazyWithRetry(() => import('./pages/b2b/Users'), 'UsersPage');

// Public router
const TrialInvitePage = lazyWithRetry(
  () => import('./pages/public/pages/TrialInvite'),
  'TrialInvitePage',
);
const TeamTrialInvitePage = lazyWithRetry(
  () => import('./pages/public/pages/TeamTrialInvite'),
  'TeamTrialInvitePage',
);
const SignInPage = lazyWithRetry(() => import('./pages/public/pages/SignIn'), 'SignInPage');
// const SignUpPage = lazyWithRetry(() => import('./pages/public/pages/PatientSignUp'), 'SignUpPage');
const SignUpV2Page = lazyWithRetry(
  () => import('./pages/public/pages/PatientSignUpV2'),
  'PatientSignUpV2Page',
);
const TrialManagerSignUpPage = lazyWithRetry(
  () => import('./pages/public/pages/TrialManagerSignUp'),
  'TrialManagerSignUpPage',
);
const SetupPasswordPage = lazyWithRetry(
  () => import('./pages/public/pages/SetupPassword'),
  'SetupPasswordPage',
);
const ForgotPasswordPage = lazyWithRetry(
  () => import('./pages/public/pages/ForgotPassword'),
  'ForgotPasswordPage',
);

// Embedded chat
const EmbeddedChatPage = lazyWithRetry(() => import('./pages/embedded-chat'), 'EmbeddedChatPage');
const EmbeddedChatV2Page = lazyWithRetry(
  () => import('./pages/embedded-chat-v2'),
  'EmbeddedChatV2Page',
);

// B2B surveys router
const SurveysPage = lazyWithRetry(() => import('./pages/b2b/Surveys'), 'SurveysPage');
const CreateSurveyPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/Create'),
  'CreateSurveyPage',
);
const SurveyPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]'),
  'SurveyPage',
);
const SurveyQuestionsPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]/pages/Questions'),
  'SurveyQuestionsPage',
);
const UpdateSurveyDetails = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]/pages/Details'),
  'UpdateSurveyDetails',
);

// Rest of the imports
const NoMatchPage = lazyWithRetry(() => import('./pages/shared/NoMatch'), 'NoMatchPage');
const MaintenancePage = lazyWithRetry(
  () => import('./pages/shared/MaintenancePage'),
  'MaintenancePage',
);
const CTDIPage = lazyWithRetry(() => import('./pages/ctdi'), 'CTDIPage');

const patientProfileRouter = [
  { index: true, element: <Navigate to={paths.profile.general} /> },
  { path: paths.profile.general, element: <GeneralPage /> },
  { path: paths.profile.password, element: <PasswordPage /> },
  { path: paths.profile.diseases, element: <DiseasesPage /> },
  { path: paths.profile.attributes, element: <AttributesPage /> },
  { path: paths.profile.notifications, element: <PatientNotificationsSettingsPage /> },
];

const patientRouter = (isOptimisticSignUpInProgress: boolean) => {
  const { bgColorLight } = useTheme();

  return [
    {
      element: <Layout hideLinks />,
      children: [{ index: true, element: <AccountProgressionPage /> }],
    },
    {
      element: (
        <FixedLayout
          hideLinks={isOptimisticSignUpInProgress}
          contentStyle={{ backgroundColor: bgColorLight }}
        />
      ),
      children: [{ index: true, path: paths.chat, element: <ChatPage /> }],
    },
    {
      element: <Layout />,
      children: [{ index: true, path: paths.notifications, element: <Notifications /> }],
    },
    {
      element: <Layout />,
      children: [
        { path: paths.profile.root, element: <ProfileLayout />, children: patientProfileRouter },
        { path: paths.profile.delete_account, element: <DeleteAccount /> },
      ],
    },
    {
      element: <Layout />,
      children: [
        {
          path: paths.trials,
          children: [
            { index: true, element: <TrialsListPage /> },
            { path: ':trialId', element: <TrialPage /> },
            { path: `${paths.surveys.root}/:surveyId`, element: <PatientSurveyPage /> },
          ],
        },
      ],
    },
  ];
};

const b2bTrialRecruitmentRouter = (currentRole?: Role) => [
  { index: true, element: <Navigate to={paths.trials} /> },
  {
    element: <B2BTrialsPage />,
    children: [
      {
        path: paths.trials,
        element: <B2BTrialsListPage />,
      },
      ...(currentRole === Role.Admin
        ? [
            {
              path: paths.requests,
              element: <RequestsListPage />,
            },
            {
              path: paths.bulk_invites,
              children: [
                { index: true, element: <BulkInvitesList /> },
                {
                  path: paths.create,
                  element: <BulkInvitePage />,
                },
              ],
            },
          ]
        : []),
    ],
  },
  {
    path: `${paths.trials}/:trialId`,
    element: <B2BTrialPage />,
    loader: trialPageLoader(queryClient),
    children: [
      { element: <EligibilityCriteriaPage /> },
      { path: paths.criterias, element: <EligibilityCriteriaPage /> },
      {
        path: paths.onboarding.criteria_table,
        element: <EligibilityCriteriaPage isTableOverview />,
      },
      {
        path: paths.onboarding.trial_overview,
        element: <EligibilityCriteriaPage isDetailsOverview />,
      },
      { path: paths.volunteers, element: <MatchingVolunteersPage /> },
      ...(currentRole === Role.TrialManager || currentRole === Role.Sponsor
        ? [{ path: paths.notifications, element: <AdminNotificationSettings /> }]
        : []),
      ...(currentRole === Role.Admin ? [{ path: paths.teams, element: <AdminTrialTeams /> }] : []),
    ],
  },
  { path: paths.request_trial, index: true, element: <RequestTrialPage /> },
  {
    index: true,
    path: `${paths.trials}/:trialId/${paths.volunteers}/:volunteerId`,
    element: <VolunteerDetailsPage />,
  },
];

const b2bProfileRouter = [
  { index: true, element: <Navigate to={paths.profile.general} /> },
  { index: true, path: paths.profile.general, element: <GeneralFormPage /> },
  { index: true, path: paths.profile.password, element: <PasswordFormPage /> },
];

const b2bHospitalsRouter = [
  { index: true, element: <HospitalsListPage /> },
  { path: ':hospitalId', element: <HospitalDetailsPage /> },
  { index: true, path: paths.create, element: <CreateHospital /> },
];

const b2bDiseasesRouter = [
  { index: true, element: <DiseasesListPage /> },
  { path: ':diseaseId', element: <DiseaseDetailsPage /> },
];

const b2bQuestionsLibraryRouter = (role: Role) => [
  { index: true, element: <QuestionsLibraryPage /> },
  ...(role === Role.Admin
    ? [{ path: paths.questions_library.create, element: <CreateQuestionPage /> }]
    : []),
  {
    path: ':questionId',
    element: <QuestionPage />,
    children: [
      { index: true, element: <Navigate to={paths.questions_library.details} /> },
      {
        path: paths.questions_library.details,
        element: <QuestionDetailsPage />,
      },
      {
        path: paths.questions_library.classification.root,
        element: <QuestionClassificationPage />,
        children: [
          {
            index: true,
            element: <Navigate to={paths.questions_library.classification.icd_codes} />,
          },
          {
            path: paths.questions_library.classification.icd_codes,
            element: <QuestionIcdCodesPage />,
          },
          {
            path: paths.questions_library.classification.tags,
            element: <QuestionTagsPage />,
          },
          {
            path: paths.questions_library.classification.starters,
            element: <QuestionStartersPage />,
          },
        ],
      },
      {
        path: paths.questions_library.trials,
        element: <QuestionTrialsPage />,
      },
      {
        path: paths.questions_library.merge,
        element: <QuestionMergePage />,
      },
      {
        path: paths.questions_library.activity,
        element: <QuestionActivityPage />,
      },
    ],
  },
  ...(role === Role.Admin
    ? [
        {
          path: `:questionId/${paths.questions_library.merge}/:mergeId`,
          element: <QuestionMergeEditorPage />,
        },
      ]
    : []),
];

const b2bTrialsLibraryRouter = [
  { index: true, element: <TrialsLibraryPage /> },
  { path: ':trialId', element: <TrialDetailsPage /> },
];

const b2bSurveysRouter = [
  { index: true, element: <SurveysPage /> },
  {
    path: paths.surveys.create,
    element: <CreateSurveyPage />,
  },
  {
    path: ':surveyId',
    element: <SurveyPage />,
    children: [
      {
        index: true,
        element: <Navigate to={paths.surveys.details} />,
      },
      {
        path: paths.surveys.details,
        element: <UpdateSurveyDetails />,
      },
      {
        path: paths.surveys.questions,
        element: <SurveyQuestionsPage />,
      },
    ],
  },
];

const b2bRouter = (currentRole?: Role) => [
  {
    element: <B2BLayout />,
    children: [
      {
        index: true,
        element: <Navigate replace to={paths.trial_recruitment} />,
      },
      { index: true, path: paths.create_team, element: <CreateTeam /> },
      {
        path: paths.trial_recruitment,
        children: b2bTrialRecruitmentRouter(currentRole),
      },
      {
        path: paths.profile.root,
        element: <B2BProfileLayout />,
        children: b2bProfileRouter,
      },
      {
        index: true,
        path: paths.notifications,
        element: <Notifications />,
      },
      ...(currentRole === Role.Admin || currentRole === Role.Reviewer
        ? [
            { path: paths.hospitals, children: b2bHospitalsRouter },
            { path: paths.diseases, children: b2bDiseasesRouter },
            {
              path: paths.questions_library.root,
              children: b2bQuestionsLibraryRouter(currentRole),
            },
            {
              path: paths.trials_library,
              children: b2bTrialsLibraryRouter,
            },
            {
              path: paths.team.root,
              children: [
                { index: true, element: <TeamsList /> },
                ...(currentRole === Role.Admin
                  ? [
                      {
                        path: ':teamId',
                        element: <TeamSettingsLayout />,
                        children: [
                          { index: true, element: <Navigate to={paths.team.general} /> },
                          { index: true, path: paths.team.general, element: <TeamGeneral /> },
                          { index: true, path: paths.team.trials, element: <TeamTrials /> },
                          { index: true, path: paths.team.users, element: <TeamUsers /> },
                        ],
                      },
                    ]
                  : []),
              ],
            },
            {
              path: paths.users,
              element: <UsersPage />,
            },
          ]
        : []),
      ...(currentRole === Role.TrialManager ||
      currentRole === Role.PatientOrg ||
      currentRole === Role.Sponsor
        ? [
            {
              path: `${paths.team.root}/:teamId`,
              element: <TeamSettingsLayout />,
              // TODO: add loader to prefetch team info for general form
              children: [
                { index: true, element: <Navigate to={paths.team.general} /> },
                { index: true, path: paths.team.general, element: <TeamGeneral /> },
                { index: true, path: paths.team.users, element: <TeamUsers /> },
                ...(currentRole === Role.TrialManager || currentRole === Role.Sponsor
                  ? [{ index: true, path: paths.team.billing, element: <TeamBilling /> }]
                  : []),
              ],
            },
          ]
        : []),
      ...(currentRole === Role.TrialManager || currentRole === Role.Sponsor
        ? [{ path: paths.onboarding.root, element: <OnboardingPage /> }]
        : []),
      ...(currentRole === Role.Admin
        ? [{ path: paths.surveys.root, children: b2bSurveysRouter }]
        : []),
    ],
  },
];

const publicPages = [
  {
    index: true,
    path: paths.trial_invite,
    element: <TrialInvitePage />,
  },
  {
    index: true,
    path: paths.team_trial_invite,
    element: <TeamTrialInvitePage />,
  },
  {
    index: true,
    path: paths.sign_in,
    element: <SignInPage />,
  },
  // TODO: fix this after we confirm new sign up works
  // {
  //   index: true,
  //   path: paths.sign_up,
  //   element: <SignUpPage />,
  // },
  {
    index: true,
    path: `${paths.b2b}/${paths.sign_up}`,
    element: <TrialManagerSignUpPage />,
  },
  {
    index: true,
    path: paths.setup_password,
    element: <SetupPasswordPage />,
  },
  {
    index: true,
    path: paths.reset_password,
    element: <ForgotPasswordPage />,
  },
];

export const router = (isOptimisticSignUpInProgress: boolean, currentRole?: Role) =>
  createBrowserRouter([
    {
      path: paths.base,
      index: true,
      errorElement: <NoMatchPage />,
      element: <Navigate to={paths.sign_in} />,
    },
    {
      path: `${paths.base}${paths.maintenance}`,
      index: true,
      element: <MaintenancePage />,
    },
    {
      path: paths.base,
      errorElement: <NoMatchPage />,
      element: <PublicPageLayout />,
      children: publicPages,
    },
    {
      path: paths.sign_up,
      element: <PublicPageLayoutV2 />,
      children: [{ index: true, element: <SignUpV2Page /> }],
    },
    {
      path: paths.embedded_chat,
      errorElement: <NoMatchPage />,
      element: <EmbeddedChatPage />,
    },
    {
      path: paths.embedded_chat_v2,
      errorElement: <NoMatchPage />,
      element: <EmbeddedChatV2Page />,
    },
    {
      path: paths.app,
      errorElement: <NoMatchPage />,
      element: (
        <PrivateRoute
          allowedRoles={[
            Role.Admin,
            Role.TrialManager,
            Role.Reviewer,
            Role.PatientOrg,
            Role.Volunteer,
            Role.Sponsor,
          ]}
        />
      ),
      children:
        currentRole === Role.Volunteer || isOptimisticSignUpInProgress
          ? patientRouter(isOptimisticSignUpInProgress)
          : b2bRouter(currentRole),
    },
    {
      path: paths.no_match,
      element: <Navigate to={paths.sign_in} state={{ from: window.location.pathname }} />,
    },
    {
      path: paths.ctdi,
      element: <CTDIPage />,
    },
  ]);
